import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
	ChevronUpIcon,
	ChevronDownIcon,
	ChevronLeftIcon,
	ChevronRightIcon,
	MagnifyingGlassIcon,
	AdjustmentsHorizontalIcon,
	InboxIcon,
	XMarkIcon,
	CheckIcon,
} from '@heroicons/react/24/outline';

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const classNames = (...classes) => classes.filter(Boolean).join(' ');

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

/**
 * Sort indicator icon for column headers
 */
const SortIcon = ({ active, direction }) => {
	if (!active) {
		return (
			<div className="flex flex-col ml-1.5 opacity-40">
				<ChevronUpIcon className="w-3 h-3 text-gray-400 -mb-1" />
				<ChevronDownIcon className="w-3 h-3 text-gray-400" />
			</div>
		);
	}
	return direction === 'asc' ? (
		<ChevronUpIcon className="w-4 h-4 text-gray-700 ml-1.5" />
	) : (
		<ChevronDownIcon className="w-4 h-4 text-gray-700 ml-1.5" />
	);
};

/**
 * Checkbox component for row selection
 */
const Checkbox = ({ checked, indeterminate, onChange, className = '' }) => {
	const ref = useRef(null);

	useEffect(() => {
		if (ref.current) {
			ref.current.indeterminate = indeterminate;
		}
	}, [indeterminate]);

	return (
		<input
			ref={ref}
			type="checkbox"
			checked={checked}
			onChange={onChange}
			className={classNames(
				'h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer',
				className,
			)}
		/>
	);
};

/**
 * Loading skeleton rows
 */
const TableSkeleton = ({ columns, rowCount = 5, selectable }) => (
	<>
		{[...Array(rowCount)].map((_, i) => (
			<tr key={i} className="animate-pulse">
				{selectable && (
					<td className="px-4 py-4">
						<div className="h-4 w-4 bg-gray-100 rounded" />
					</td>
				)}
				{columns.map((col, j) => (
					<td key={j} className="px-6 py-4">
						<div
							className="h-4 bg-gray-100 rounded"
							style={{ width: col.skeletonWidth || '60%' }}
						/>
					</td>
				))}
			</tr>
		))}
	</>
);

/**
 * Empty state display
 */
const EmptyState = ({
	colSpan,
	icon: Icon = InboxIcon,
	title = 'No data found',
	description = 'Try adjusting your search or filter criteria',
}) => (
	<tr>
		<td colSpan={colSpan} className="px-6 py-16">
			<div className="flex flex-col items-center text-center">
				<Icon className="w-12 h-12 text-gray-300 mb-4" />
				<p className="text-sm font-medium text-gray-900 mb-1">{title}</p>
				<p className="text-sm text-gray-500">{description}</p>
			</div>
		</td>
	</tr>
);

/**
 * Filter dropdown component
 */
const FilterDropdown = ({ label, value, options, onChange, placeholder }) => {
	const [isOpen, setIsOpen] = useState(false);
	const dropdownRef = useRef(null);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	const selectedOption = options.find((opt) => opt.value === value);

	return (
		<div className="relative" ref={dropdownRef}>
			<button
				onClick={() => setIsOpen(!isOpen)}
				className={classNames(
					'inline-flex items-center gap-2 px-3 py-2 bg-white border rounded-lg text-sm hover:bg-gray-50 transition-colors',
					value
						? 'border-indigo-300 text-indigo-700'
						: 'border-gray-200 text-gray-700',
				)}
			>
				{selectedOption?.label || placeholder || label}
				<ChevronDownIcon
					className={classNames(
						'w-4 h-4 transition-transform',
						isOpen ? 'rotate-180' : '',
					)}
				/>
			</button>
			{isOpen && (
				<div className="absolute left-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
					{options.map((option) => (
						<button
							key={option.value}
							onClick={() => {
								onChange(option.value);
								setIsOpen(false);
							}}
							className={classNames(
								'flex items-center justify-between w-full px-4 py-2 text-left text-sm hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg',
								value === option.value
									? 'text-indigo-700 bg-indigo-50'
									: 'text-gray-700',
							)}
						>
							{option.label}
							{value === option.value && (
								<CheckIcon className="w-4 h-4 text-indigo-600" />
							)}
						</button>
					))}
				</div>
			)}
		</div>
	);
};

/**
 * Pagination component
 */
const Pagination = ({
	currentPage,
	totalPages,
	totalItems,
	pageSize,
	onPageChange,
	onPageSizeChange,
	pageSizeOptions = [10, 25, 50, 100],
}) => {
	const startItem = (currentPage - 1) * pageSize + 1;
	const endItem = Math.min(currentPage * pageSize, totalItems);

	return (
		<div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
			<div className="flex items-center gap-4">
				<span className="text-sm text-gray-500">
					Showing {startItem}-{endItem} of {totalItems}
				</span>
				{onPageSizeChange && (
					<select
						value={pageSize}
						onChange={(e) => onPageSizeChange(Number(e.target.value))}
						className="text-sm border-gray-200 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
					>
						{pageSizeOptions.map((size) => (
							<option key={size} value={size}>
								{size} per page
							</option>
						))}
					</select>
				)}
			</div>
			<div className="flex items-center gap-2">
				<button
					onClick={() => onPageChange(currentPage - 1)}
					disabled={currentPage === 1}
					className={classNames(
						'p-2 rounded-lg transition-colors',
						currentPage === 1
							? 'text-gray-300 cursor-not-allowed'
							: 'text-gray-500 hover:bg-gray-100 hover:text-gray-700',
					)}
				>
					<ChevronLeftIcon className="w-5 h-5" />
				</button>
				<span className="text-sm text-gray-700 px-2">
					Page {currentPage} of {totalPages}
				</span>
				<button
					onClick={() => onPageChange(currentPage + 1)}
					disabled={currentPage === totalPages}
					className={classNames(
						'p-2 rounded-lg transition-colors',
						currentPage === totalPages
							? 'text-gray-300 cursor-not-allowed'
							: 'text-gray-500 hover:bg-gray-100 hover:text-gray-700',
					)}
				>
					<ChevronRightIcon className="w-5 h-5" />
				</button>
			</div>
		</div>
	);
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * NextGen DataTable Component
 *
 * A comprehensive, feature-rich data table with:
 * - Sorting (single/multi-column)
 * - Filtering
 * - Search
 * - Pagination (client-side or server-side)
 * - Row selection
 * - Loading states
 * - Empty states
 * - Responsive design
 *
 * @example
 * ```jsx
 * <DataTable
 *   data={users}
 *   columns={[
 *     { key: 'name', header: 'Name', sortable: true },
 *     { key: 'email', header: 'Email' },
 *     { key: 'status', header: 'Status', render: (row) => <Badge>{row.status}</Badge> },
 *   ]}
 *   onRowClick={(row) => console.log(row)}
 *   searchable
 *   selectable
 * />
 * ```
 */
const DataTable = ({
	// Data
	data = [],
	columns = [],
	keyField = 'id',
	loading = false,

	// Features
	searchable = false,
	searchPlaceholder = 'Search...',
	searchFields = [],
	selectable = false,
	sortable = true,
	filterable = false,
	filters = [],

	// Pagination
	pagination = false,
	pageSize: initialPageSize = 10,
	pageSizeOptions = [10, 25, 50, 100],
	serverSidePagination = false,
	totalItems: externalTotalItems,
	currentPage: externalCurrentPage,
	onPageChange: externalOnPageChange,

	// Sorting
	defaultSort = null,
	serverSideSorting = false,
	onSortChange,

	// Selection
	selectedRows: externalSelectedRows,
	onSelectionChange,

	// Filtering
	onFilterChange,

	// Events
	onRowClick,

	// Styling
	className = '',
	containerClassName = '',
	headerClassName = '',
	rowClassName = '',
	striped = false,
	hoverable = true,
	bordered = true,
	compact = false,
	textSize = 'xs', // 'xs', 'sm', 'md'

	// Empty/Loading
	emptyIcon,
	emptyTitle,
	emptyDescription,
	skeletonRows = 5,

	// Actions
	actions = null,
	rowActions = null,
}) => {
	// ============================================================================
	// STATE
	// ============================================================================

	const [search, setSearch] = useState('');
	const [sortConfig, setSortConfig] = useState(defaultSort);
	const [internalPage, setInternalPage] = useState(1);
	const [internalPageSize, setInternalPageSize] = useState(initialPageSize);
	const [internalSelectedRows, setInternalSelectedRows] = useState([]);
	const [activeFilters, setActiveFilters] = useState({});

	// Use external or internal state for controlled/uncontrolled patterns
	const currentPage = serverSidePagination ? externalCurrentPage : internalPage;
	const pageSize = internalPageSize;
	const selectedRows = externalSelectedRows ?? internalSelectedRows;

	// ============================================================================
	// DATA PROCESSING
	// ============================================================================

	// Filter data based on search
	const searchedData = useMemo(() => {
		if (!search || serverSidePagination) return data;

		const query = search.toLowerCase();
		const fieldsToSearch =
			searchFields.length > 0
				? searchFields
				: columns
						.filter((col) => col.searchable !== false)
						.map((col) => col.key);

		return data.filter((row) =>
			fieldsToSearch.some((field) => {
				const value = row[field];
				if (value == null) return false;
				return String(value).toLowerCase().includes(query);
			}),
		);
	}, [data, search, searchFields, columns, serverSidePagination]);

	// Filter data based on active filters
	const filteredData = useMemo(() => {
		if (Object.keys(activeFilters).length === 0 || serverSidePagination) {
			return searchedData;
		}

		return searchedData.filter((row) =>
			Object.entries(activeFilters).every(([key, value]) => {
				if (!value || value === 'all') return true;
				return row[key] === value;
			}),
		);
	}, [searchedData, activeFilters, serverSidePagination]);

	// Sort data
	const sortedData = useMemo(() => {
		if (!sortConfig || serverSideSorting) return filteredData;

		return [...filteredData].sort((a, b) => {
			const aVal = a[sortConfig.key];
			const bVal = b[sortConfig.key];
			const direction = sortConfig.direction === 'asc' ? 1 : -1;

			if (aVal == null) return 1;
			if (bVal == null) return -1;

			if (typeof aVal === 'number' && typeof bVal === 'number') {
				return (aVal - bVal) * direction;
			}

			if (aVal instanceof Date && bVal instanceof Date) {
				return (aVal.getTime() - bVal.getTime()) * direction;
			}

			return String(aVal).localeCompare(String(bVal)) * direction;
		});
	}, [filteredData, sortConfig, serverSideSorting]);

	// Paginate data
	const paginatedData = useMemo(() => {
		if (!pagination || serverSidePagination) return sortedData;

		const startIndex = (currentPage - 1) * pageSize;
		return sortedData.slice(startIndex, startIndex + pageSize);
	}, [sortedData, pagination, currentPage, pageSize, serverSidePagination]);

	// Calculate total pages
	const totalItems = serverSidePagination
		? externalTotalItems
		: sortedData.length;
	const totalPages = Math.ceil(totalItems / pageSize);

	// ============================================================================
	// HANDLERS
	// ============================================================================

	const handleSort = useCallback(
		(key) => {
			const newConfig = {
				key,
				direction:
					sortConfig?.key === key && sortConfig?.direction === 'asc'
						? 'desc'
						: 'asc',
			};
			setSortConfig(newConfig);

			if (serverSideSorting && onSortChange) {
				onSortChange(newConfig);
			}
		},
		[sortConfig, serverSideSorting, onSortChange],
	);

	const handlePageChange = useCallback(
		(page) => {
			if (serverSidePagination && externalOnPageChange) {
				externalOnPageChange(page);
			} else {
				setInternalPage(page);
			}
		},
		[serverSidePagination, externalOnPageChange],
	);

	const handlePageSizeChange = useCallback((size) => {
		setInternalPageSize(size);
		setInternalPage(1);
	}, []);

	const handleSelectAll = useCallback(() => {
		const allIds = paginatedData.map((row) => row[keyField]);
		const allSelected = allIds.every((id) => selectedRows.includes(id));

		const newSelection = allSelected
			? selectedRows.filter((id) => !allIds.includes(id))
			: [...new Set([...selectedRows, ...allIds])];

		if (onSelectionChange) {
			onSelectionChange(newSelection);
		} else {
			setInternalSelectedRows(newSelection);
		}
	}, [paginatedData, keyField, selectedRows, onSelectionChange]);

	const handleSelectRow = useCallback(
		(rowId) => {
			const newSelection = selectedRows.includes(rowId)
				? selectedRows.filter((id) => id !== rowId)
				: [...selectedRows, rowId];

			if (onSelectionChange) {
				onSelectionChange(newSelection);
			} else {
				setInternalSelectedRows(newSelection);
			}
		},
		[selectedRows, onSelectionChange],
	);

	const handleFilterChange = useCallback(
		(key, value) => {
			const newFilters = { ...activeFilters, [key]: value };
			setActiveFilters(newFilters);
			setInternalPage(1);

			if (onFilterChange) {
				onFilterChange(newFilters);
			}
		},
		[activeFilters, onFilterChange],
	);

	const handleClearFilters = useCallback(() => {
		setActiveFilters({});
		setSearch('');
		setInternalPage(1);

		if (onFilterChange) {
			onFilterChange({});
		}
	}, [onFilterChange]);

	// ============================================================================
	// COMPUTED VALUES
	// ============================================================================

	const allPageRowsSelected = useMemo(() => {
		if (paginatedData.length === 0) return false;
		return paginatedData.every((row) => selectedRows.includes(row[keyField]));
	}, [paginatedData, selectedRows, keyField]);

	const somePageRowsSelected = useMemo(() => {
		if (paginatedData.length === 0) return false;
		const selectedCount = paginatedData.filter((row) =>
			selectedRows.includes(row[keyField]),
		).length;
		return selectedCount > 0 && selectedCount < paginatedData.length;
	}, [paginatedData, selectedRows, keyField]);

	const hasActiveFilters =
		Object.values(activeFilters).some((v) => v && v !== 'all') || search;

	const totalColumns =
		columns.length + (selectable ? 1 : 0) + (rowActions ? 1 : 0);

	// Text size classes
	const textSizeClasses = {
		xs: { header: 'text-xs', cell: 'text-xs' },
		sm: { header: 'text-xs', cell: 'text-sm' },
		md: { header: 'text-sm', cell: 'text-base' },
	};
	const currentTextSize = textSizeClasses[textSize] || textSizeClasses.xs;

	// ============================================================================
	// RENDER
	// ============================================================================

	return (
		<div className={classNames('bg-white rounded-lg', containerClassName)}>
			{/* Toolbar */}
			{(searchable || filterable || actions || hasActiveFilters) && (
				<div
					className={classNames(
						'flex flex-wrap items-center gap-4 p-4',
						bordered ? 'border-b border-gray-100' : '',
					)}
				>
					{/* Search */}
					{searchable && (
						<div className="flex-1 min-w-[200px] relative">
							<MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
							<input
								type="text"
								value={search}
								onChange={(e) => {
									setSearch(e.target.value);
									setInternalPage(1);
								}}
								placeholder={searchPlaceholder}
								className="w-full pl-10 pr-4 py-2.5 text-sm bg-gray-50 border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 placeholder:text-gray-400"
							/>
							{search && (
								<button
									onClick={() => setSearch('')}
									className="absolute right-3 top-1/2 -translate-y-1/2"
								>
									<XMarkIcon className="w-4 h-4 text-gray-400 hover:text-gray-600" />
								</button>
							)}
						</div>
					)}

					{/* Filters */}
					{filterable && filters.length > 0 && (
						<div className="flex items-center gap-2">
							<AdjustmentsHorizontalIcon className="w-5 h-5 text-gray-400" />
							{filters.map((filter) => (
								<FilterDropdown
									key={filter.key}
									label={filter.label}
									placeholder={filter.placeholder}
									value={activeFilters[filter.key]}
									options={filter.options}
									onChange={(value) => handleFilterChange(filter.key, value)}
								/>
							))}
							{hasActiveFilters && (
								<button
									onClick={handleClearFilters}
									className="text-sm text-gray-500 hover:text-gray-700 px-2"
								>
									Clear all
								</button>
							)}
						</div>
					)}

					{/* Actions */}
					{actions && <div className="flex items-center gap-2">{actions}</div>}
				</div>
			)}

			{/* Selection Info */}
			{selectable && selectedRows.length > 0 && (
				<div className="flex items-center justify-between px-4 py-2 bg-indigo-50 border-b border-indigo-100">
					<span className="text-sm text-indigo-700">
						{selectedRows.length} row{selectedRows.length !== 1 ? 's' : ''}{' '}
						selected
					</span>
					<button
						onClick={() => {
							if (onSelectionChange) {
								onSelectionChange([]);
							} else {
								setInternalSelectedRows([]);
							}
						}}
						className="text-sm text-indigo-600 hover:text-indigo-800"
					>
						Clear selection
					</button>
				</div>
			)}

			{/* Table */}
			<div className="overflow-x-auto">
				<table className={classNames('w-full', className)}>
					<thead>
						<tr
							className={classNames(
								'border-b border-gray-100',
								headerClassName,
							)}
						>
							{/* Selection checkbox */}
							{selectable && (
								<th className="w-12 px-4 py-4">
									<Checkbox
										checked={allPageRowsSelected}
										indeterminate={somePageRowsSelected}
										onChange={handleSelectAll}
									/>
								</th>
							)}

							{/* Column headers */}
							{columns.map((column) => {
								const isSortable = sortable && column.sortable !== false;
								const isSorted = sortConfig?.key === column.key;

								return (
									<th
										key={column.key}
										onClick={
											isSortable ? () => handleSort(column.key) : undefined
										}
										className={classNames(
											'px-6 text-left font-medium text-gray-500 uppercase tracking-wider',
											currentTextSize.header,
											compact ? 'py-3' : 'py-4',
											isSortable
												? 'cursor-pointer select-none hover:text-gray-700'
												: '',
											column.headerClassName,
										)}
										style={{ width: column.width }}
									>
										<div className="flex items-center text-xxs">
											{column.header}
											{isSortable && (
												<SortIcon
													active={isSorted}
													direction={sortConfig?.direction}
												/>
											)}
										</div>
									</th>
								);
							})}

							{/* Row actions header */}
							{rowActions && <th className="w-12 px-4 py-4" />}
						</tr>
					</thead>
					<tbody>
						{loading ? (
							<TableSkeleton
								columns={columns}
								rowCount={skeletonRows}
								selectable={selectable}
							/>
						) : paginatedData.length === 0 ? (
							<EmptyState
								colSpan={totalColumns}
								icon={emptyIcon}
								title={emptyTitle}
								description={emptyDescription}
							/>
						) : (
							paginatedData.map((row, rowIndex) => {
								const rowId = row[keyField];
								const isSelected = selectedRows.includes(rowId);

								return (
									<tr
										key={rowId}
										onClick={onRowClick ? () => onRowClick(row) : undefined}
										className={classNames(
											'border-b border-gray-50 transition-colors',
											onRowClick ? 'cursor-pointer' : '',
											hoverable ? 'hover:bg-gray-50/50' : '',
											striped && rowIndex % 2 === 1 ? 'bg-gray-50/30' : '',
											isSelected ? 'bg-indigo-50/50' : '',
											typeof rowClassName === 'function'
												? rowClassName(row, rowIndex)
												: rowClassName,
										)}
									>
										{/* Selection checkbox */}
										{selectable && (
											<td className="px-4" onClick={(e) => e.stopPropagation()}>
												<Checkbox
													checked={isSelected}
													onChange={() => handleSelectRow(rowId)}
												/>
											</td>
										)}

										{/* Data cells */}
										{columns.map((column) => (
											<td
												key={column.key}
												className={classNames(
													'px-6',
													currentTextSize.cell,
													compact ? 'py-2' : 'py-4',
													column.cellClassName,
												)}
											>
												{column.render
													? column.render(row, rowIndex)
													: row[column.key]}
											</td>
										))}

										{/* Row actions */}
										{rowActions && (
											<td className="px-4" onClick={(e) => e.stopPropagation()}>
												{typeof rowActions === 'function'
													? rowActions(row, rowIndex)
													: rowActions}
											</td>
										)}
									</tr>
								);
							})
						)}
					</tbody>
				</table>
			</div>

			{/* Pagination */}
			{pagination && totalPages > 0 && !loading && (
				<Pagination
					currentPage={currentPage}
					totalPages={totalPages}
					totalItems={totalItems}
					pageSize={pageSize}
					onPageChange={handlePageChange}
					onPageSizeChange={handlePageSizeChange}
					pageSizeOptions={pageSizeOptions}
				/>
			)}
		</div>
	);
};

export default DataTable;
